import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { Send, Phone, Mail, Clock, MessageCircle, ArrowLeft } from "lucide-react";
import { useToast } from "@/shared/hooks/use-toast";

const CustomerSupportPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    category: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.category || !formData.message) {
      toast({
        title: "Required Fields Missing",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: "Support Request Submitted!",
        description: "We've received your request and will respond within 24 hours.",
      });
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        subject: "",
        category: "",
        message: ""
      });
    } catch (error) {
      console.error('Support submission error:', error);
      toast({
        title: "Error",
        description: "Failed to submit support request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = () => {
    navigate('/auth?mode=signup', { state: { from: '/customer-support' } });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-b border-border z-50">
        <div className="container mx-auto px-6 py-2">
          <div className="flex items-center justify-between">
            {/* Left side navigation */}
            <nav className="flex items-center space-x-8">
              <button 
                onClick={() => {
                  navigate('/');
                  setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                }}
                className="text-2xl font-bold text-primary hover:text-primary/90 transition-colors"
              >
                wlkthru
              </button>
              <div className="hidden md:flex space-x-8">
                <button
                  onClick={() => navigate('/features')}
                  className="text-sm font-medium transition-colors hover:text-primary text-foreground"
                >
                  Features
                </button>
                <button
                  onClick={() => navigate('/pricing')}
                  className="text-sm font-medium transition-colors hover:text-primary text-foreground"
                >
                  Pricing
                </button>
              </div>
            </nav>

            {/* Right side buttons */}
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/auth')}
                className="text-sm text-muted-foreground px-3 py-2 hover:text-foreground transition-colors cursor-pointer"
              >
                Log in
              </button>
              <button 
                onClick={handleGetStarted}
                className="text-sm px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors cursor-pointer"
              >
                Get started for free
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Back Button */}
            <div className="mb-8">
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </button>
            </div>
            
            <div className="text-center">
              <h1 className="text-5xl md:text-6xl font-bold text-foreground leading-tight mb-8">
                Customer{" "}
                <span className="text-primary">Support</span>
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed mb-12 max-w-3xl mx-auto">
                We're here to help you get the most out of wlkthru. Get in touch with our support team for assistance with any questions or issues.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="pb-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
            {/* Support Form */}
            <div className="lg:col-span-2">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl">Submit a Support Request</CardTitle>
                  <CardDescription>
                    Fill out the form below and our support team will get back to you within 24 hours.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          placeholder="Your full name"
                          value={formData.name}
                          onChange={(e) => handleInputChange("name", e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="your@email.com"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="category">Category *</Label>
                      <Select onValueChange={(value) => handleInputChange("category", value)} value={formData.category}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="technical">Technical Issue</SelectItem>
                          <SelectItem value="billing">Billing & Payment</SelectItem>
                          <SelectItem value="feature">Feature Request</SelectItem>
                          <SelectItem value="account">Account Management</SelectItem>
                          <SelectItem value="demo">Demo Request</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject *</Label>
                      <Input
                        id="subject"
                        placeholder="Brief description of your issue"
                        value={formData.subject}
                        onChange={(e) => handleInputChange("subject", e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        placeholder="Please provide detailed information about your issue or question"
                        value={formData.message}
                        onChange={(e) => handleInputChange("message", e.target.value)}
                        rows={6}
                        className="resize-none"
                        required
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full"
                      disabled={loading}
                    >
                      {loading ? (
                        "Submitting..."
                      ) : (
                        <>
                          Submit Request
                          <Send className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info & Quick Help */}
            <div className="space-y-8">
              {/* Contact Information */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-start gap-4">
                    <Mail className="h-5 w-5 text-primary mt-1" />
                    <div>
                      <h3 className="font-semibold">Email Support</h3>
                      <p className="text-muted-foreground">support@wlkthru.co</p>
                      <p className="text-sm text-muted-foreground">Response within 24 hours</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <Phone className="h-5 w-5 text-primary mt-1" />
                    <div>
                      <h3 className="font-semibold">Phone Support</h3>
                      <button
                        onClick={() => setShowPhoneModal(true)}
                        className="text-primary hover:text-primary/80 transition-colors"
                      >
                        Click to view phone number
                      </button>
                      <p className="text-sm text-muted-foreground">Monday - Friday, 8:00 AM - 5:00 PM MST</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <Clock className="h-5 w-5 text-primary mt-1" />
                    <div>
                      <h3 className="font-semibold">Business Hours</h3>
                      <p className="text-muted-foreground">
                        Monday - Friday<br />
                        8:00 AM - 5:00 PM MST
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Help */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle>Quick Help</CardTitle>
                  <CardDescription>
                    Get instant answers to common questions
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                    <h4 className="font-semibold text-primary mb-2 flex items-center gap-2">
                      <MessageCircle className="h-4 w-4" />
                      Live Chat
                    </h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      Chat with our support team in real-time for immediate assistance.
                    </p>
                    <Button variant="outline" size="sm" className="w-full">
                      Start Live Chat
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-semibold">Frequently Asked Questions</h4>
                    <div className="space-y-2">
                      <div className="p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors">
                        <p className="text-sm font-medium">How do I schedule a video call?</p>
                      </div>
                      <div className="p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors">
                        <p className="text-sm font-medium">How do I manage my subscription?</p>
                      </div>
                      <div className="p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors">
                        <p className="text-sm font-medium">How do I add team members?</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Phone Support Modal */}
      <Dialog open={showPhoneModal} onOpenChange={setShowPhoneModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Phone Support</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-primary">+1-385-200-3122</p>
              <p className="text-sm text-muted-foreground mt-2">
                Our support team is available to help you with any questions or issues.
              </p>
            </div>
            <div className="bg-muted/50 p-3 rounded-lg text-center">
              <p className="font-medium text-sm mb-1">Business Hours:</p>
              <p className="text-sm text-muted-foreground">Monday - Friday</p>
              <p className="text-sm text-muted-foreground">8:00 AM - 5:00 PM MST</p>
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowPhoneModal(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="py-12 pb-6 bg-primary">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-white">
            {/* Brand Section */}
            <div className="space-y-4">
              <button 
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-4xl font-bold hover:text-white/80 transition-colors cursor-pointer"
              >
                wlkthru
              </button>
              <div className="w-16 h-0.5 bg-white/30"></div>
              <div className="space-y-3">
                <p className="text-white/90">Check us out on:</p>
                <div className="flex gap-2">
                  <a 
                    href="https://www.facebook.com/profile.php?id=61578600642587" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">f</span>
                  </a>
                  <a 
                    href="https://www.instagram.com/wlkthru.co/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">ig</span>
                  </a>
                  <a 
                    href="https://www.linkedin.com/company/wlkthruco/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">in</span>
                  </a>
                  <a 
                    href="https://www.youtube.com/@wlkthruco" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">yt</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Get in Touch and Legal */}
            <div className="flex flex-col md:flex-row gap-8 md:col-span-2 justify-end">
              {/* Get in Touch */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-white">Get in Touch</h4>
                <ul className="space-y-2">
                  <li><a href="/customer-support" className="text-white/80 hover:text-white transition-colors">• Customer Support</a></li>
                  <li><a href="/contact" className="text-white/80 hover:text-white transition-colors">• Demo Request</a></li>
                </ul>
              </div>

              {/* Legal */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-white">Legal</h4>
                <ul className="space-y-2">
                  <li><button onClick={() => { navigate('/legal?section=privacy'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="text-white/80 hover:text-white transition-colors text-left">• Privacy Policy</button></li>
                  <li><button onClick={() => { navigate('/legal?section=terms'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="text-white/80 hover:text-white transition-colors text-left">• Terms and Conditions</button></li>
                  <li><button onClick={() => { navigate('/legal?section=disclaimer'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="text-white/80 hover:text-white transition-colors text-left">• Disclaimer</button></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-4 pt-2 border-t border-white/20 text-center">
            <p className="text-white/60 text-xs font-light">
              Copyright © 2025 wlkthru | All rights reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CustomerSupportPage;
